import { useEffect, useMemo, useState } from 'react';
import { Plus, Trash2, ChevronDown, ChevronUp, SlidersHorizontal } from 'lucide-react';
import { Section, SettingRow } from '@vnetwork/ui/layout';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardBody,
  CardFooter,
  Button,
  IconButton,
  Input,
  Switch,
  Skeleton,
  Badge,
  Checkbox,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { StatusPill, EmptyState } from '@vnetwork/ui/data';
import { useToast } from '@vnetwork/ui/feedback';
import { useCdnConfig, useUpdateCdnConfig, useDeleteCdnConfig } from '@/api/manticore';
import { trackFeatureAction } from '@/lib/analytics-events';
import { configTone, configLabel } from '../shared';
import { jsonEqual } from '../_shared/ConfigFormShell';
import { CdnAspectDrift } from '../_shared/VendorDrift';

export function OptimizationTab({ domain, domainId }: { domain: string; domainId: string }) {
  return (
    <Section title="Optimization" description={`Compression, image optimization, and timeouts for ${domain}.`}>
      <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
        <CompressionCard domainId={domainId} />
        <ImageOptCard domainId={domainId} />
        <TimeoutsCard domainId={domainId} />
      </div>
    </Section>
  );
}

function useConfigCard(domainId: string, path: string, markers: string[]) {
  const { data: cfg, isLoading } = useCdnConfig(domainId, path, { vendorMarkers: markers });
  const isConfigured = !!cfg;
  const update = useUpdateCdnConfig(domainId, path);
  const remove = useDeleteCdnConfig(domainId, path);
  return { cfg, isLoading, isConfigured, update, remove, busy: update.isPending || remove.isPending };
}

function CardActions({ isConfigured, dirty, busy, saving, onSave, onRemove }: { isConfigured: boolean; dirty: boolean; busy: boolean; saving: boolean; onSave: () => void; onRemove: () => void }) {
  return (
    <CardFooter style={{ display: 'flex', justifyContent: 'space-between' }}>
      {isConfigured ? <Button variant="secondary" disabled={busy} onClick={onRemove}>Remove</Button> : <span />}
      <Button disabled={(isConfigured && !dirty) || busy} onClick={onSave}>{saving ? 'Saving…' : isConfigured ? 'Save' : 'Create'}</Button>
    </CardFooter>
  );
}

// ── Compression ──────────────────────────────────────────────────────────
const ALGOS = ['gzip', 'br', 'zstd'];
function CompressionCard({ domainId }: { domainId: string }) {
  const { show } = useToast();
  const { cfg, isLoading, isConfigured, update, remove, busy } = useConfigCard(domainId, 'compression', ['enabled', 'algorithms']);
  const [form, setForm] = useState({ enabled: false, algorithms: ['gzip', 'br'] as string[], min_size_bytes: 1024, content_type_filter: [] as string[] });
  const [snap, setSnap] = useState('');
  useEffect(() => {
    const next = {
      enabled: Boolean(cfg?.enabled),
      algorithms: Array.isArray(cfg?.algorithms) ? (cfg!.algorithms as string[]) : ['gzip', 'br'],
      min_size_bytes: Number(cfg?.min_size_bytes ?? 1024),
      content_type_filter: Array.isArray(cfg?.content_type_filter) ? (cfg!.content_type_filter as string[]) : [],
    };
    setForm(next);
    setSnap(JSON.stringify(next));
  }, [cfg]);
  const dirty = useMemo(() => !jsonEqual(form, JSON.parse(snap || '{}')), [form, snap]);
  const toggleAlgo = (a: string) => setForm((f) => ({ ...f, algorithms: f.algorithms.includes(a) ? f.algorithms.filter((x) => x !== a) : [...f.algorithms, a] }));

  if (isLoading) return <Skeleton style={{ height: 160 }} />;
  return (
    <Card>
      <CardHeader actions={<StatusPill tone={configTone(isConfigured)}>{form.enabled ? 'Enabled' : configLabel(isConfigured)}</StatusPill>}>
        <CardTitle>Compression</CardTitle><CardDescription>Compress responses at the edge.</CardDescription>
      </CardHeader>
      <CardBody style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
        <CdnAspectDrift domainId={domainId} path="compression" />
        <SettingRow title="Enabled" control={<Switch checked={form.enabled} onCheckedChange={(v) => setForm((f) => ({ ...f, enabled: v }))} />} />
        <div>
          <div style={{ fontSize: 'var(--vn-font-size-md)', fontWeight: 'var(--vn-font-weight-semibold)', marginBottom: 'var(--vn-space-2)' }}>Algorithms</div>
          <div style={{ display: 'flex', gap: 'var(--vn-space-4)' }}>
            {ALGOS.map((a) => (
              <label key={a} style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-1)', fontSize: 'var(--vn-font-size-lg)', cursor: 'pointer' }}>
                <Checkbox checked={form.algorithms.includes(a)} onCheckedChange={() => toggleAlgo(a)} /> {a}
              </label>
            ))}
          </div>
        </div>
        <div style={{ display: 'grid', gap: 'var(--vn-space-4)', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}>
          <Field label="Minimum size (bytes)">{(p) => <Input {...p} type="number" value={String(form.min_size_bytes)} onChange={(e) => setForm((f) => ({ ...f, min_size_bytes: Number(e.target.value) || 0 }))} />}</Field>
          <Field label="Content-Type filter" description="Comma-separated; empty = defaults.">{(p) => <Input {...p} value={form.content_type_filter.join(', ')} onChange={(e) => setForm((f) => ({ ...f, content_type_filter: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) }))} />}</Field>
        </div>
      </CardBody>
      <CardActions isConfigured={isConfigured} dirty={dirty} busy={busy} saving={update.isPending}
        onSave={() => update.mutate(form as unknown as Record<string, unknown>, { onSuccess: () => { trackFeatureAction('cdn', 'compression', isConfigured ? 'edit' : 'create'); show({ tone: 'success', title: 'Compression saved' }); } })}
        onRemove={() => remove.mutate(undefined, { onSuccess: () => { trackFeatureAction('cdn', 'compression', 'delete'); show({ tone: 'success', title: 'Compression removed' }); } })} />
    </Card>
  );
}

// ── Image Optimization ───────────────────────────────────────────────────
const FORMATS = ['original', 'webp', 'avif', 'jpeg', 'png'];
const IMG_MATCH = ['prefix', 'exact', 'suffix', 'contains', 'wildcard', 'regex'];
interface ImgRule { _id: string; name: string; priority: number; enabled: boolean; output_format: string; quality: number; match_type: string; match_path: string; max_width: number; max_height: number; match_extensions: string[] }
let _ig = 0;
const iid = () => `ig${++_ig}`;
function ImageOptCard({ domainId }: { domainId: string }) {
  const { show } = useToast();
  const { cfg, isLoading, isConfigured, update, remove, busy } = useConfigCard(domainId, 'image-optimization', ['enabled', 'rules']);
  const [enabled, setEnabled] = useState(false);
  const [rules, setRules] = useState<ImgRule[]>([]);
  const [snap, setSnap] = useState('');
  const [open, setOpen] = useState<Set<string>>(new Set());
  useEffect(() => {
    const rawRules = Array.isArray(cfg?.rules) ? (cfg!.rules as Record<string, unknown>[]) : [];
    const next = {
      enabled: Boolean(cfg?.enabled),
      rules: rawRules.map((r) => ({
        _id: iid(), name: (r.name as string) || '', priority: Number(r.priority) || 0, enabled: r.enabled == null ? true : Boolean(r.enabled),
        output_format: (r.output_format as string) || 'webp', quality: Number(r.quality) || 80, match_type: (r.match_type as string) || 'prefix',
        match_path: (r.match_path as string) || '', max_width: Number(r.max_width) || 0, max_height: Number(r.max_height) || 0,
        match_extensions: Array.isArray(r.match_extensions) ? (r.match_extensions as string[]) : [],
      })),
    };
    setEnabled(next.enabled);
    setRules(next.rules);
    setSnap(JSON.stringify({ enabled: next.enabled, rules: next.rules }));
  }, [cfg]);
  const dirty = useMemo(() => !jsonEqual({ enabled, rules }, JSON.parse(snap || '{}')), [enabled, rules, snap]);
  const patch = (id: string, c: Partial<ImgRule>) => setRules((p) => p.map((r) => (r._id === id ? { ...r, ...c } : r)));

  if (isLoading) return <Skeleton style={{ height: 160 }} />;
  return (
    <Card>
      <CardHeader
        actions={
          <Button variant="secondary" size="sm" startIcon={<Plus size={14} />} onClick={() => { const r = { _id: iid(), name: '', priority: rules.length ? Math.max(...rules.map((x) => x.priority)) + 10 : 100, enabled: true, output_format: 'webp', quality: 80, match_type: 'prefix', match_path: '/', max_width: 0, max_height: 0, match_extensions: ['jpg', 'png'] }; setRules((p) => [...p, r]); setOpen((s) => new Set(s).add(r._id)); }}>Add rule</Button>
        }
      >
        <CardTitle>Image optimization</CardTitle><CardDescription>Transcode and resize images on the fly.</CardDescription>
      </CardHeader>
      <CardBody style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
        <CdnAspectDrift domainId={domainId} path="image-optimization" />
        <SettingRow title="Enabled" control={<Switch checked={enabled} onCheckedChange={setEnabled} />} />
        {rules.length === 0 ? (
          <EmptyState size="sm" icon={<SlidersHorizontal size={20} />} title="No image rules yet" description="Add a rule to optimize matching images." />
        ) : (
          rules.map((r) => {
            const isOpen = open.has(r._id);
            return (
              <div key={r._id} style={{ border: '1px solid var(--vn-border-default)', borderRadius: 'var(--vn-radius-md)', overflow: 'hidden' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-3)', padding: 'var(--vn-space-2) var(--vn-space-3)' }}>
                  <Badge tone="neutral" appearance="subtle" size="sm">{r.priority}</Badge>
                  <span style={{ flex: 1, fontWeight: 'var(--vn-font-weight-medium)' }}>{r.name || 'Untitled'}</span>
                  <Badge tone="info" appearance="subtle" size="sm">{r.output_format} · q{r.quality}</Badge>
                  <IconButton variant="ghost" size="sm" aria-label="Remove" onClick={() => setRules((p) => p.filter((x) => x._id !== r._id))}><Trash2 size={15} /></IconButton>
                  <IconButton variant="ghost" size="sm" aria-label="Toggle" onClick={() => setOpen((s) => { const n = new Set(s); n.has(r._id) ? n.delete(r._id) : n.add(r._id); return n; })}>{isOpen ? <ChevronUp size={15} /> : <ChevronDown size={15} />}</IconButton>
                </div>
                {isOpen && (
                  <div style={{ borderTop: '1px solid var(--vn-border-default)', padding: 'var(--vn-space-4)', display: 'grid', gap: 'var(--vn-space-4)', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
                    <Field label="Name">{(p) => <Input {...p} value={r.name} onChange={(e) => patch(r._id, { name: e.target.value })} />}</Field>
                    <Field label="Priority">{(p) => <Input {...p} type="number" value={String(r.priority)} onChange={(e) => patch(r._id, { priority: Number(e.target.value) || 0 })} />}</Field>
                    <Field label="Output format">
                      {(p) => <Select value={r.output_format} onValueChange={(v) => patch(r._id, { output_format: v })}><SelectTrigger {...p}><SelectValue /></SelectTrigger><SelectContent>{FORMATS.map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent></Select>}
                    </Field>
                    <Field label="Quality">{(p) => <Input {...p} type="number" value={String(r.quality)} onChange={(e) => patch(r._id, { quality: Number(e.target.value) || 0 })} />}</Field>
                    <Field label="Match type">
                      {(p) => <Select value={r.match_type} onValueChange={(v) => patch(r._id, { match_type: v })}><SelectTrigger {...p}><SelectValue /></SelectTrigger><SelectContent>{IMG_MATCH.map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent></Select>}
                    </Field>
                    <Field label="Match path">{(p) => <Input {...p} value={r.match_path} onChange={(e) => patch(r._id, { match_path: e.target.value })} />}</Field>
                    <Field label="Max width">{(p) => <Input {...p} type="number" value={String(r.max_width)} onChange={(e) => patch(r._id, { max_width: Number(e.target.value) || 0 })} />}</Field>
                    <Field label="Max height">{(p) => <Input {...p} type="number" value={String(r.max_height)} onChange={(e) => patch(r._id, { max_height: Number(e.target.value) || 0 })} />}</Field>
                    <Field label="Extensions" description="Comma-separated.">{(p) => <Input {...p} value={r.match_extensions.join(', ')} onChange={(e) => patch(r._id, { match_extensions: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) })} />}</Field>
                  </div>
                )}
              </div>
            );
          })
        )}
      </CardBody>
      <CardActions isConfigured={isConfigured} dirty={dirty} busy={busy} saving={update.isPending}
        onSave={() => update.mutate({ enabled, rules: rules.map(({ _id, ...r }) => r) }, { onSuccess: () => { trackFeatureAction('cdn', 'image_opt', isConfigured ? 'edit' : 'create'); show({ tone: 'success', title: 'Image optimization saved' }); } })}
        onRemove={() => remove.mutate(undefined, { onSuccess: () => { trackFeatureAction('cdn', 'image_opt', 'delete'); show({ tone: 'success', title: 'Image optimization removed' }); } })} />
    </Card>
  );
}

// ── Timeouts ─────────────────────────────────────────────────────────────
const TIMEOUT_FIELDS: [string, string][] = [
  ['client_read_secs', 'Client read (s)'],
  ['client_write_secs', 'Client write (s)'],
  ['server_connect_secs', 'Server connect (s)'],
  ['server_read_secs', 'Server read (s)'],
  ['keepalive_secs', 'Keepalive (s)'],
  ['websocket_idle_secs', 'WebSocket idle (s)'],
];
function TimeoutsCard({ domainId }: { domainId: string }) {
  const { show } = useToast();
  const { cfg, isLoading, isConfigured, update, remove, busy } = useConfigCard(domainId, 'timeouts', ['server_read_secs', 'client_read_secs']);
  const [form, setForm] = useState<Record<string, number>>({});
  const [snap, setSnap] = useState('');
  useEffect(() => {
    const next: Record<string, number> = {};
    TIMEOUT_FIELDS.forEach(([k]) => (next[k] = Number(cfg?.[k] ?? 0)));
    setForm(next);
    setSnap(JSON.stringify(next));
  }, [cfg]);
  const dirty = useMemo(() => !jsonEqual(form, JSON.parse(snap || '{}')), [form, snap]);
  if (isLoading) return <Skeleton style={{ height: 140 }} />;
  return (
    <Card>
      <CardHeader actions={<StatusPill tone={configTone(isConfigured)}>{configLabel(isConfigured)}</StatusPill>}>
        <CardTitle>Timeouts</CardTitle><CardDescription>Connection timeout limits, in seconds.</CardDescription>
      </CardHeader>
      <CardBody style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
        <CdnAspectDrift domainId={domainId} path="timeouts" />
        <div style={{ display: 'grid', gap: 'var(--vn-space-4)', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
          {TIMEOUT_FIELDS.map(([k, label]) => (
            <Field key={k} label={label}>{(p) => <Input {...p} type="number" value={String(form[k] ?? 0)} onChange={(e) => setForm((f) => ({ ...f, [k]: Number(e.target.value) || 0 }))} />}</Field>
          ))}
        </div>
      </CardBody>
      <CardActions isConfigured={isConfigured} dirty={dirty} busy={busy} saving={update.isPending}
        onSave={() => update.mutate(form, { onSuccess: () => { trackFeatureAction('cdn', 'timeouts', isConfigured ? 'edit' : 'create'); show({ tone: 'success', title: 'Timeouts saved' }); } })}
        onRemove={() => remove.mutate(undefined, { onSuccess: () => { trackFeatureAction('cdn', 'timeouts', 'delete'); show({ tone: 'success', title: 'Timeouts removed' }); } })} />
    </Card>
  );
}
